/** Bus stop → General tab. Scalar fields (name, label, description, warning,
 *  lat/lng — plain number fields; the embedded map is deferred) via RHF; the
 *  image (tri-state), `enabled`, the tags picker, and the ordered `attractions[]`
 *  (each an ATTRACTION-type Activity with a distance in metres) are held in
 *  local state. Stops carry `updated_ts` → the save sends an
 *  `expected_updated_ts` token and a 409 surfaces the ConflictBanner. */
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box, Checkbox, FormControlLabel, IconButton, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import * as activitiesApi from '@/api/activities';
import { ACTIVITY_TYPE, type ActivityRow } from '@/api/activities';
import type { RouteStopDetail, RouteStopWriteJson } from '@/api/routeStops';
import * as toursApi from '@/api/tours';
import type { PickerOption } from '@/api/tours';
import { ConflictBanner, EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { ImageUploadField, type ImageFieldValue } from '@/components/ImageUploadField/ImageUploadField';
import { LatLngPicker } from '@/components/LatLngPicker/LatLngPicker';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import {
  attractionActions,
  catalogTagActions,
  latLngActions,
  type CatalogCtx,
} from '../../catalog/catalogLinks';
import { useRouteStopPatch } from '../hooks/useRouteStopPatch';

const Schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  label: z.string().nullable(),
  description: z.string().nullable(),
  warning: z.string().nullable(),
  latitude: z.string().nullable(),
  longitude: z.string().nullable(),
});

type FormValues = z.infer<typeof Schema>;

const numStr = (n: number | null | undefined): string => (n == null ? '' : String(n));
const toNum = (s: string | null): number | null => {
  if (s == null || s.trim() === '') return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};

function toDefaults(s: RouteStopDetail): FormValues {
  return {
    name: s.name,
    label: s.label ?? '',
    description: s.description ?? '',
    warning: s.warning ?? '',
    latitude: numStr(s.latitude),
    longitude: numStr(s.longitude),
  };
}

/** A local attraction row — carries the display name for the table + the
 *  editable distance (metres). Sequence is the array position on save. */
interface AttractionRow {
  attraction_id: number;
  name: string | null;
  distance: number;
}

function seedAttractions(xs: RouteStopDetail['attractions']): AttractionRow[] {
  return [...xs]
    .sort((a, b) => a.sequence - b.sequence)
    .map((a) => ({ attraction_id: a.attraction_id, name: a.name, distance: a.distance }));
}

function seedTags(xs: { id: number; name: string | null }[]): PickerOption[] {
  return xs.map((t) => ({ id: t.id, name: t.name }));
}

const tagIds = (xs: PickerOption[]) => xs.map((t) => t.id).sort((a, b) => a - b);
const sameTags = (a: PickerOption[], b: { id: number }[]) => {
  const bs = b.map((x) => x.id).sort((x, y) => x - y);
  return a.length === b.length && tagIds(a).every((v, i) => v === bs[i]);
};

/** Attractions are dirty when the ordered (id, distance) pairs differ. */
const attractionsKey = (xs: { attraction_id: number; distance: number }[]) =>
  JSON.stringify(xs.map((a) => [a.attraction_id, a.distance]));

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>{title}</Typography>
      {children}
    </Box>
  );
}

export function BusStopGeneralTab({ stop, onSaved }: { stop: RouteStopDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_fleet_manage');
  const { control, handleSubmit, reset, formState, watch, setValue } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: toDefaults(stop),
  });
  const patch = useRouteStopPatch(stop, onSaved);

  const [image, setImage] = useState<ImageFieldValue>(null);
  const [enabled, setEnabled] = useState(stop.enabled);
  const [tags, setTags] = useState<PickerOption[]>(() => seedTags(stop.tags));
  const [attractions, setAttractions] = useState<AttractionRow[]>(() => seedAttractions(stop.attractions));

  const resetToRecord = () => {
    reset(toDefaults(stop));
    setImage(null);
    setEnabled(stop.enabled);
    setTags(seedTags(stop.tags));
    setAttractions(seedAttractions(stop.attractions));
  };
  useEffect(() => {
    resetToRecord();
    // Re-seed whenever the server record changes (incl. after a save → refetch,
    // so the form isn't stuck "dirty"). Keyed on the whole record object —
    // React Query hands us a fresh top-level ref only when a field changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop]);

  const extrasDirty = useMemo(() => {
    if (enabled !== stop.enabled) return true;
    if (image instanceof File || image === 'cleared') return true;
    if (!sameTags(tags, stop.tags)) return true;
    if (attractionsKey(attractions) !== attractionsKey(stop.attractions)) return true;
    return false;
  }, [enabled, image, tags, attractions, stop]);

  const moveAttraction = (i: number, d: -1 | 1) =>
    setAttractions((rs) => {
      const j = i + d;
      if (j < 0 || j >= rs.length) return rs;
      const copy = [...rs];
      [copy[i], copy[j]] = [copy[j]!, copy[i]!];
      return copy;
    });

  const submit = handleSubmit((v) => {
    const body: RouteStopWriteJson = {
      name: v.name.trim(),
      label: v.label?.trim() || null,
      description: v.description?.trim() || null,
      warning: v.warning?.trim() || null,
      latitude: toNum(v.latitude),
      longitude: toNum(v.longitude),
      enabled,
      tags: tags.map((t) => ({ id: t.id })),
      attractions: attractions.map((a, i) => ({
        attraction_id: a.attraction_id,
        sequence: i,
        distance: a.distance,
      })),
    };

    let file: File | null = null;
    if (image instanceof File) file = image;
    else if (image === 'cleared') body.image_id = null;

    patch.save(body, file);
  });

  const err = formState.errors;
  const dirty = formState.isDirty || extrasDirty;
  const ctx: CatalogCtx = { tenantId: stop.tenant.id };
  const lat = toNum(watch('latitude'));
  const lng = toNum(watch('longitude'));

  return (
    <form onSubmit={submit}>
      <ConflictBanner
        open={patch.conflict}
        onReload={onSaved}
        message="This bus stop changed on the server (another editor). Reload before saving."
      />

      <Section title="Basics">
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          <Controller control={control} name="name" render={({ field }) => (
            <TextField {...field} size="small" fullWidth disabled={!canManage}
              label="Name" error={!!err.name} helperText={err.name?.message} />
          )} />
          <Controller control={control} name="label" render={({ field }) => (
            <TextField {...field} value={field.value ?? ''} size="small" sx={{ width: 160 }} disabled={!canManage}
              label="Label" inputProps={{ maxLength: 64 }}
              error={!!err.label} helperText={err.label?.message || 'Short pin label'} />
          )} />
        </Box>
        <Controller control={control} name="description" render={({ field }) => (
          <TextField {...field} value={field.value ?? ''} size="small" fullWidth multiline minRows={2} sx={{ mb: 1.5 }}
            disabled={!canManage} label="Description" error={!!err.description} helperText={err.description?.message} />
        )} />
        <Controller control={control} name="warning" render={({ field }) => (
          <TextField {...field} value={field.value ?? ''} size="small" fullWidth multiline minRows={2}
            disabled={!canManage} label="Warning" error={!!err.warning} helperText={err.warning?.message} />
        )} />
      </Section>

      <Section title="Location">
        <LatLngPicker
          size="small"
          disabled={!canManage}
          value={{ lat: toNum(watch('latitude')), lng: toNum(watch('longitude')) }}
          onChange={(v) => {
            setValue('latitude', v.lat == null ? '' : String(v.lat), { shouldDirty: true });
            setValue('longitude', v.lng == null ? '' : String(v.lng), { shouldDirty: true });
          }}
        />
        <PickerLinks
          items={
            lat != null && lng != null
              ? [{ key: 'latlng', label: `${lat}, ${lng}`, actions: latLngActions({ lat, lng }) }]
              : []
          }
        />
      </Section>

      <Section title="Image">
        <ImageUploadField
          label="Stop image"
          imageRef={stop.image}
          value={image}
          onChange={setImage}
          canManage={canManage}
        />
      </Section>

      <Section title="Status">
        <FormControlLabel
          control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} size="small" disabled={!canManage} />}
          label="Enabled"
        />
      </Section>

      <Section title="Tags">
        <RemoteAutocomplete<PickerOption>
          label="Tags" size="small" multiple fetchOnEmpty value={tags}
          getOptionLabel={(o) => o.name ?? `#${o.id}`} getOptionKey={(o) => o.id}
          queryFn={async (q) => (await toursApi.picker(stop.tenant.id, 'tags', q)).results}
          onChange={(v) => setTags((v as PickerOption[]) ?? [])}
        />
        {/* The picker keeps its chips; each tag's links live underneath. */}
        <PickerLinks
          items={tags.map((t) => ({
            key: t.id,
            label: t.name ?? `#${t.id}`,
            actions: catalogTagActions(t, ctx),
          }))}
        />
      </Section>

      <Section title="Nearby attractions">
        {attractions.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.disabled', mb: 1.5 }}>No attractions.</Typography>
        ) : (
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableHead>
              <TableRow>
                <TableCell>Attraction</TableCell>
                <TableCell sx={{ width: 160 }}>Distance (m)</TableCell>
                {canManage && <TableCell align="right" sx={{ width: 120 }}>Order</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {attractions.map((a, i) => (
                <TableRow key={a.attraction_id}>
                  <TableCell>
                    {/* An attraction IS an activity (type 5) — the row reaches
                        its page rather than naming it. */}
                    <ValueActions actions={attractionActions({ id: a.attraction_id, name: a.name })}>
                      {a.name ?? `#${a.attraction_id}`}
                    </ValueActions>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number" size="small" variant="standard" value={a.distance} disabled={!canManage}
                      onChange={(e) =>
                        setAttractions((rs) => rs.map((x, j) => (j === i ? { ...x, distance: Number(e.target.value) || 0 } : x)))
                      }
                    />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => moveAttraction(i, -1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => moveAttraction(i, 1)} disabled={i === attractions.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setAttractions((rs) => rs.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {canManage && (
          <Box sx={{ maxWidth: 420 }}>
            <RemoteAutocomplete<ActivityRow>
              label="Add attraction" size="small" fetchOnEmpty value={null}
              getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
              queryFn={async (q) => {
                const r = await activitiesApi.list({
                  tour_operator_id: stop.tenant.id,
                  type: ACTIVITY_TYPE.ATTRACTION,
                  search: q || undefined,
                  pageSize: 50,
                });
                return r.results.filter((o) => !attractions.some((x) => x.attraction_id === o.id));
              }}
              onChange={(v) => {
                const opt = v as ActivityRow | null;
                if (!opt) return;
                setAttractions((rs) => [...rs, { attraction_id: opt.id, name: opt.name, distance: 0 }]);
              }}
            />
          </Box>
        )}
      </Section>

      <EditorSaveBar
        dirty={dirty} saving={patch.saving} canManage={canManage}
        onSave={submit} onDiscard={resetToRecord}
      />
    </form>
  );
}
